import {
  FaLaptop,
  FaTools,
  FaHandshake,
  FaUsers,
  FaSmile,
  FaCheckCircle,
  FaWrench,
} from "react-icons/fa";
import { useRef, useEffect, useState } from "react";

// Intersection Observer hook
function useOnScreen(ref, rootMargin = "0px") {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new window.IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting),
      { rootMargin }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, rootMargin]);

  return isIntersecting;
}

export default function HowItWorks() {
  const sectionRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  const isVisible = useOnScreen(sectionRef, "-60px"); // start a bit before fully visible

  useEffect(() => {
    if (isVisible && !hasAnimated) setHasAnimated(true);
  }, [isVisible, hasAnimated]);

  // Make vertical initial movement longer for a more dramatic upward animation
  const initialY = 28; // px (increase this for slower/more visible upward effect)
  const transitionDuration = 1400; // ms, double the default for slower effect

  // Animation classes for steps/statistics
  // We'll use inline transform and opacity for a more customized effect
  const getAnimStyle = (delay = 0) => ({
    transition: `all ${transitionDuration}ms cubic-bezier(0.33,0.75,0.68,1)`,
    transitionDelay: hasAnimated ? `${delay}ms` : "0ms",
    transform: hasAnimated ? "translateY(0)" : `translateY(${initialY}px)`,
    opacity: hasAnimated ? 1 : 0,
  });

  return (
    <section
      ref={sectionRef}
      className="relative py-20 bg-cover bg-center font-PTSerif-Regular"
      style={{
        backgroundImage: "url('/images/repair-bg.jpg')",
      }}
    >
      <div className="relative max-w-7xl mx-auto px-6 font-PTSerif-Regular">
        {/* Heading */}
        <div
          className="text-center text-black mb-16 font-PTSerif-Regular transition-all"
          style={getAnimStyle(0)}
        >
          <p className="text-gray-300 font-PTSerif-Regular">How It Works?</p>
          <h2 className="text-4xl font-bold mt-2 font-PTSerif-Regular">
            THREE STEPS SOLUTION
          </h2>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-8 -mb-24 font-PTSerif-Regular">
          {/* Step 1 */}
          <div
            className={`bg-blue-500 text-white p-10 text-center shadow-lg font-PTSerif-Regular`}
            style={getAnimStyle(150)}
          >
            <FaLaptop className="text-5xl mx-auto mb-6" />

            <h3 className="text-xl font-semibold mb-4 font-PTSerif-Regular">
              Contact & Book Service
            </h3>

            <p className="text-sm font-PTSerif-Regular">
              Call our team and schedule IT support, AMC service,
              networking setup, CCTV installation or device repair.
            </p>
          </div>

          {/* Step 2 */}
          <div
            className={`bg-white text-gray-800 p-10 text-center shadow-lg font-PTSerif-Regular`}
            style={getAnimStyle(400)}
          >
            <FaTools className="text-5xl mx-auto mb-6 text-blue-500" />

            <h3 className="text-xl font-semibold mb-4 font-PTSerif-Regular">
              Diagnose The Problem
            </h3>

            <p className="text-sm text-gray-600 font-PTSerif-Regular">
              Our expert engineers analyze your computer, laptop,
              network, server or CCTV system and identify the issue.
            </p>
          </div>

          {/* Step 3 */}
          <div
            className={`bg-black text-white p-10 text-center shadow-lg font-PTSerif-Regular`}
            style={getAnimStyle(700)}
          >
            <FaHandshake className="text-5xl mx-auto mb-6 text-blue-400" />

            <h3 className="text-xl font-semibold mb-4 font-PTSerif-Regular">
              Repair & Deliver Solution
            </h3>

            <p className="text-sm text-gray-300 font-PTSerif-Regular">
              We repair, configure or install the system and ensure
              everything works perfectly before delivery.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Stats Section */}
      <div className="bg-white pt-36  font-PTSerif-Regular">
        <div className="max-w-6xl mx-auto grid md:grid-cols-4 gap-10 text-center font-PTSerif-Regular">
          <div
            className={`flex flex-col items-center font-PTSerif-Regular`}
            style={getAnimStyle(1000)}
          >
            <FaUsers className="text-blue-500 text-3xl mb-2" />
            <h4 className="text-2xl font-bold font-PTSerif-Regular">10+</h4>
            <p className="text-gray-600 text-sm font-PTSerif-Regular">Years Service</p>
          </div>

          <div
            className={`flex flex-col items-center font-PTSerif-Regular`}
            style={getAnimStyle(1150)}
          >
            <FaSmile className="text-blue-500 text-3xl mb-2" />
            <h4 className="text-2xl font-bold font-PTSerif-Regular">2000+</h4>
            <p className="text-gray-600 text-sm font-PTSerif-Regular">Happy Clients</p>
          </div>

          <div
            className={`flex flex-col items-center font-PTSerif-Regular`}
            style={getAnimStyle(1300)}
          >
            <FaCheckCircle className="text-blue-500 text-3xl mb-2" />
            <h4 className="text-2xl font-bold font-PTSerif-Regular">98%</h4>
            <p className="text-gray-600 text-sm font-PTSerif-Regular">Issues Resolved</p>
          </div>

          <div
            className={`flex flex-col items-center font-PTSerif-Regular`}
            style={getAnimStyle(1450)}
          >
            <FaWrench className="text-blue-500 text-3xl mb-2" />
            <h4 className="text-2xl font-bold font-PTSerif-Regular">1500+</h4>
            <p className="text-gray-600 text-sm font-PTSerif-Regular">Devices Serviced</p>
          </div>
        </div>
      </div>
    </section>
  );
}